import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, X, Globe, Copy, RefreshCw, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { http, apiError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { useAuth } from "@/context/AuthContext";
import { Card, SectionTitle, Spinner } from "@/components/Kit";
import { GradeChip, VerifyChip } from "@/components/WebKit";

export default function WebTargets() {
  const { orgId } = useOrg();
  const { can } = useAuth();
  const canManage = can("web.targets", "manage");
  const navigate = useNavigate();
  const [targets, setTargets] = useState([]);
  const [show, setShow] = useState(false);
  const [edit, setEdit] = useState(null);

  const load = useCallback(() => http.get("/web/targets", { params: { org_id: orgId } }).then((r) => setTargets(r.data)), [orgId]);
  useEffect(() => { setTargets([]); }, [orgId]);
  useEffect(() => { if (orgId) load(); }, [load, orgId]);

  const remove = async (t, e) => {
    e.stopPropagation();
    if (!window.confirm(`Remove ${t.hostname}? This archives the target and its scans.`)) return;
    try { await http.delete(`/web/targets/${t.id}`, { params: { org_id: orgId } }); toast.success("Target removed"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div className="p-6 max-w-[1500px] mx-auto fade-up">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-display text-3xl font-bold text-[#A5300F]">Web Targets</h1>
          <p className="text-sm text-[#8A8175] mt-1">{targets.length} live sites under exposure &amp; integrity monitoring.</p>
        </div>
        {canManage && (
          <button onClick={() => setShow(true)} data-testid="new-target-btn"
            className="flex items-center gap-2 bg-[#A5300F] text-white font-semibold text-sm px-4 py-2 rounded-md hover:bg-[#8F2A0D]">
            <Plus className="w-4 h-4" /> Add Target
          </button>
        )}
      </div>

      <Card>
        <table className="w-full text-sm" data-testid="targets-table">
          <thead><tr className="text-[11px] uppercase tracking-wider text-[#8A8175] text-left bg-[#FFF5EB] border-b border-[#ECE6DD]">
            <th className="py-2.5 px-4">Hostname</th><th>Authorisation</th><th>Auth expiry</th><th className="text-center">Monitoring</th>
            <th className="text-center">Grade</th><th>Last scan</th><th className="text-center">Open C / H</th>{canManage && <th className="text-right pr-4">Actions</th>}
          </tr></thead>
          <tbody>
            {targets.map((t) => (
              <tr key={t.id} onClick={() => navigate(`/web/targets/${t.id}`)}
                className="border-b border-[#F4EFE8] hover:bg-[#FBF7F1] cursor-pointer" data-testid={`target-row-${t.id}`}>
                <td className="py-3 px-4 font-semibold flex items-center gap-2"><Globe className="w-4 h-4 text-[#B5AC9E]" />{t.hostname}</td>
                <td><VerifyChip verified={t.authorisation?.verified} active={t.auth_active} /></td>
                <td className="text-xs text-[#8A8175]">{t.authorisation?.expires_at ? new Date(t.authorisation.expires_at).toLocaleDateString("en-GB") : "—"}</td>
                <td className="text-center text-xs">{t.monitoring_enabled ? `Every ${t.monitoring_interval_minutes}m` : "Off"}</td>
                <td className="text-center"><GradeChip grade={t.posture_grade} score={t.posture_score} /></td>
                <td className="text-xs text-[#8A8175]">{t.last_scan_at ? new Date(t.last_scan_at).toLocaleString("en-GB") : "Never"}</td>
                <td className="text-center font-semibold">
                  <span style={{ color: "#A5300F" }}>{t.open_critical}</span> / <span style={{ color: "#D55816" }}>{t.open_high}</span>
                </td>
                {canManage && (
                  <td className="text-right pr-4 whitespace-nowrap">
                    <button onClick={(e) => { e.stopPropagation(); setEdit(t); }} data-testid={`edit-target-${t.id}`} className="p-1.5 text-[#8A8175] hover:text-[#A5300F]"><Pencil className="w-4 h-4" /></button>
                    <button onClick={(e) => remove(t, e)} data-testid={`delete-target-${t.id}`} className="p-1.5 text-[#8A8175] hover:text-[#A5300F]"><Trash2 className="w-4 h-4" /></button>
                  </td>
                )}
              </tr>
            ))}
            {targets.length === 0 && <tr><td colSpan={canManage ? 8 : 7} className="py-10 text-center text-[#B5AC9E]">No targets yet. Add a site to begin exposure &amp; integrity monitoring.</td></tr>}
          </tbody>
        </table>
      </Card>

      {show && <AddTargetModal orgId={orgId} onClose={() => setShow(false)} onSaved={() => { setShow(false); load(); }} />}
      {edit && <EditTargetModal orgId={orgId} target={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}

function EditTargetModal({ orgId, target, onClose, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ label: target.label || "", monitoring_enabled: !!target.monitoring_enabled,
    monitoring_interval_minutes: target.monitoring_interval_minutes || 60 });
  const save = async () => {
    setBusy(true);
    try {
      await http.patch(`/web/targets/${target.id}`, {
        label: f.label, monitoring_enabled: f.monitoring_enabled,
        monitoring_interval_minutes: Number(f.monitoring_interval_minutes) || 60,
      }, { params: { org_id: orgId } });
      toast.success("Target updated"); onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-start justify-center z-50 p-4 overflow-y-auto" onClick={onClose}>
      <Card className="w-full max-w-md my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#ECE6DD]">
          <SectionTitle>Edit {target.hostname}</SectionTitle>
          <button onClick={onClose}><X className="w-4 h-4 text-[#8A8175]" /></button>
        </div>
        <div className="p-5 space-y-3">
          <Field label="Label"><input data-testid="edit-target-label" value={f.label} onChange={(e) => setF((p) => ({ ...p, label: e.target.value }))} className={inp} /></Field>
          <label className="flex items-center gap-2 text-sm pt-1">
            <input type="checkbox" data-testid="edit-target-monitoring" checked={f.monitoring_enabled} onChange={(e) => setF((p) => ({ ...p, monitoring_enabled: e.target.checked }))} className="accent-[#A5300F]" />
            Monitoring enabled
          </label>
          {f.monitoring_enabled && (
            <Field label="Interval (minutes)"><input type="number" min="5" value={f.monitoring_interval_minutes} onChange={(e) => setF((p) => ({ ...p, monitoring_interval_minutes: e.target.value }))} className={inp} /></Field>
          )}
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-[#ECE6DD]">
          <button onClick={onClose} className="px-3 py-2 text-sm border border-[#E7D9C6] rounded-md">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="save-target-edit-btn" className="px-4 py-2 text-sm bg-[#A5300F] text-white font-semibold rounded-md hover:bg-[#8F2A0D] disabled:opacity-50 flex items-center gap-2">
            {busy && <Spinner />} Save
          </button>
        </div>
      </Card>
    </div>
  );
}

function AddTargetModal({ orgId, onClose, onSaved }) {
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [target, setTarget] = useState(null);
  const [verify, setVerify] = useState(null);
  const [f, setF] = useState({ hostname: "", label: "", authorised_by_name: "", authorised_by_email: "",
    organisation: "", scope_notes: "", expires_at: "", method: "dns_txt" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await http.post("/web/targets", {
        org_id: orgId, hostname: f.hostname, label: f.label || f.hostname,
        authorisation: { authorised_by_name: f.authorised_by_name, authorised_by_email: f.authorised_by_email,
          organisation: f.organisation, scope_notes: f.scope_notes, method: f.method,
          expires_at: f.expires_at ? new Date(f.expires_at).toISOString() : null },
      });
      setTarget(data);
      const v = await http.post(`/web/targets/${data.id}/verify`, {}, { params: { org_id: orgId } });
      setVerify(v.data); setStep(2);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const recheck = async () => {
    setBusy(true);
    try {
      const v = await http.post(`/web/targets/${target.id}/verify`, {}, { params: { org_id: orgId } });
      setVerify(v.data);
      if (v.data.verified) toast.success("Target verified");
      else toast.error("Verification token not found yet");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-start justify-center z-50 p-4 overflow-y-auto" onClick={onClose}>
      <Card className="w-full max-w-lg my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#ECE6DD]">
          <SectionTitle>{step === 1 ? "Add Web Target" : "Verify Ownership"}</SectionTitle>
          <button onClick={onClose} data-testid="close-target-modal"><X className="w-4 h-4 text-[#8A8175]" /></button>
        </div>

        {step === 1 && (
          <div className="p-5 space-y-3">
            <Field label="Hostname"><input data-testid="target-hostname" placeholder="example.com" value={f.hostname} onChange={(e) => set("hostname", e.target.value)} className={inp} /></Field>
            <Field label="Label"><input data-testid="target-label" value={f.label} onChange={(e) => set("label", e.target.value)} className={inp} /></Field>
            <div className="pt-1 text-[11px] uppercase tracking-wider text-[#8A8175] font-semibold">Authorisation</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Authorised by (name)"><input data-testid="auth-name" value={f.authorised_by_name} onChange={(e) => set("authorised_by_name", e.target.value)} className={inp} /></Field>
              <Field label="Email"><input data-testid="auth-email" value={f.authorised_by_email} onChange={(e) => set("authorised_by_email", e.target.value)} className={inp} /></Field>
            </div>
            <Field label="Organisation"><input value={f.organisation} onChange={(e) => set("organisation", e.target.value)} className={inp} /></Field>
            <Field label="Scope notes"><textarea rows={2} value={f.scope_notes} onChange={(e) => set("scope_notes", e.target.value)} className={inp} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Method">
                <select value={f.method} onChange={(e) => set("method", e.target.value)} className={inp} data-testid="auth-method">
                  <option value="dns_txt">DNS TXT record</option>
                  <option value="file_token">File token</option>
                  <option value="written">Written</option>
                </select>
              </Field>
              <Field label="Expires at"><input type="date" data-testid="auth-expiry" value={f.expires_at} onChange={(e) => set("expires_at", e.target.value)} className={inp} /></Field>
            </div>
          </div>
        )}

        {step === 2 && verify && (
          <div className="p-5 space-y-4" data-testid="verify-step">
            <div className={`text-sm font-semibold ${verify.verified ? "text-[#2E7D32]" : "text-[#A5300F]"}`}>
              {verify.verified ? "✓ Ownership verified — you may now scan this target." : "Awaiting verification. Publish one of the tokens below, then re-check."}
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#8A8175] font-semibold mb-1">Option A — DNS TXT record</div>
              <div className="flex items-center gap-2 bg-[#F6F1EA] border border-[#ECE6DD] rounded p-2 text-xs font-mono break-all">
                <span className="flex-1">{verify.dns_record}</span>
                <button onClick={() => copy(verify.dns_record)}><Copy className="w-3.5 h-3.5 text-[#8A8175]" /></button>
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#8A8175] font-semibold mb-1">Option B — File at</div>
              <div className="flex items-center gap-2 bg-[#F6F1EA] border border-[#ECE6DD] rounded p-2 text-xs font-mono break-all">
                <span className="flex-1">{verify.file_url}</span>
                <button onClick={() => copy(verify.token)}><Copy className="w-3.5 h-3.5 text-[#8A8175]" /></button>
              </div>
              <div className="text-[11px] text-[#8A8175] mt-1">File body: <span className="font-mono">{verify.token}</span></div>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-[#ECE6DD]">
          {step === 1 && <>
            <button onClick={onClose} className="px-3 py-2 text-sm border border-[#E7D9C6] rounded-md">Cancel</button>
            <button onClick={create} disabled={busy || !f.hostname || !f.authorised_by_name || !f.authorised_by_email} data-testid="create-target-btn"
              className="px-4 py-2 text-sm bg-[#A5300F] text-white font-semibold rounded-md hover:bg-[#8F2A0D] disabled:opacity-50 flex items-center gap-2">
              {busy && <Spinner />} Create &amp; continue
            </button>
          </>}
          {step === 2 && <>
            <button onClick={recheck} disabled={busy} data-testid="recheck-btn"
              className="px-3 py-2 text-sm border border-[#E7D9C6] rounded-md flex items-center gap-2"><RefreshCw className="w-3.5 h-3.5" /> Re-check</button>
            <button onClick={onSaved} data-testid="finish-target-btn"
              className="px-4 py-2 text-sm bg-[#A5300F] text-white font-semibold rounded-md hover:bg-[#8F2A0D]">Done</button>
          </>}
        </div>
      </Card>
    </div>
  );
}

const inp = "w-full mt-1 bg-white border border-[#E7D9C6] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#A5300F]";
const Field = ({ label, children }) => (
  <div><label className="text-[11px] uppercase tracking-wider text-[#8A8175] font-semibold">{label}</label>{children}</div>
);
